import type { ITemplateNode } from '@ifs-gcp/core';
import { isTabGroupNode, isTabNode } from '@ifs-gcp/core/helpers';
import type {
  PageChild,
  TabCardChild,
  TabPanelChild,
  TabPanelsChild,
  TabsChild,
} from '@ifs-gcp/core/models';

import type { TabElement } from '@/types/auditTabs';

export const getTabCardProps = (
  children: ITemplateNode<TabCardChild>['children'] | ITemplateNode<PageChild>['children'],
): {
  cardTitle: ITemplateNode<TabsChild>;
  cardBody: ITemplateNode<TabPanelChild>[];
} => {
  const cardTitle = children.find((e): e is ITemplateNode<TabsChild> => e.type === 'tabs');

  if (!cardTitle) {
    throw new Error('[getTabCardProps]: Could not find "tabs" element');
  }

  const cardBody = children.find(
    (e): e is ITemplateNode<TabPanelsChild> => e.type === 'tab-panels',
  )?.children;

  if (!cardBody) {
    throw new Error('[getTabCardProps]: Could not find "tab-panels" element');
  }

  return { cardTitle, cardBody };
};

export const createTabs = (auditTemplate: ITemplateNode<PageChild>) => {
  const {
    cardTitle: { children },
  } = getTabCardProps(auditTemplate.children);

  return children.reduce<TabElement[]>((acc, tab) => {
    if (isTabNode(tab)) {
      if (!tab.props.name) {
        return acc;
      }

      const newTab: TabElement = {
        id: tab.props.value,
        label: tab.props.name,
      };

      return [...acc, newTab];
    }

    if (isTabGroupNode(tab)) {
      const groupIds = tab.children.map(({ props: { value } }) => value);

      const groupedTabs = tab.children.reduce<TabElement[]>((tabGroup, subTab) => {
        if (!subTab.props.name) {
          return tabGroup;
        }

        const newTab: TabElement = {
          id: subTab.props.value,
          label: subTab.props.name,
        };

        if (groupIds) {
          newTab.groupIds = groupIds;
        }

        return [...tabGroup, newTab];
      }, []);

      return [...acc, ...(groupedTabs || [])];
    }

    return acc;
  }, []);
};
